import React, { useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

type DisplayedFilters = Record<string, boolean>;
type SetFilters = (filters: Record<string, any>, displayedFilters: DisplayedFilters) => void;

type Args = {
  setFilters: SetFilters;
  displayedFilters: DisplayedFilters;
  filterToQuery: (searchText: string) => Record<string, any>;
};

export const useAutocompleteSearch = ({ setFilters, displayedFilters, filterToQuery }: Args) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [isInputOverflowed, setIsInputOverflowed] = useState(false);

  const debouncedSetFilters = useMemo(
    () =>
      debounce((value: string) => {
        setFilters(filterToQuery(value), displayedFilters);
      }, 500),
    [setFilters, filterToQuery, displayedFilters],
  );

  useEffect(() => () => debouncedSetFilters.cancel(), [debouncedSetFilters]);

  const onInputChange = (event: React.SyntheticEvent, newValue: string, reason: string) => {
    if (reason === 'input') {
      const { scrollWidth, clientWidth } = event.target as HTMLInputElement;
      setInputValue(newValue);
      setIsInputOverflowed((prev) => Boolean(newValue) && (prev || scrollWidth > clientWidth));
      debouncedSetFilters(newValue);
    }
  };

  const reset = () => {
    setFilters({}, displayedFilters);
    setInputValue('');
    setIsInputOverflowed(false);
  };

  return { inputValue, isInputOverflowed, onInputChange, reset };
};
